import { extractTrackSegments, findClosestSampleIndex } from './splitTimes';

const FEET_PER_MILE = 5280;
const SEARCH_THRESHOLD_FEET = 200;
const LOOKBACK_FEET = 300;
const LOOKAHEAD_FEET = 900;

const THROTTLE_THRESHOLDS = {
  onset: 8,
  full: 90,
  hesitationDrop: 12,
  hesitationMinSamples: 3,
};

// When using longG as throttle proxy, positive longG = acceleration = throttle application
const LONG_G_THROTTLE_THRESHOLDS = {
  onset: 0.08,
  full: 0.35,
  hesitationDrop: 0.06,
  hesitationMinSamples: 3,
};

function toFeet(miles) {
  return miles * FEET_PER_MILE;
}

function average(values) {
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function spread(values) {
  if (!values.length) return null;
  return Math.max(...values) - Math.min(...values);
}

function gradeFromScore(score) {
  if (score >= 92) return 'A';
  if (score >= 82) return 'B';
  if (score >= 70) return 'C';
  if (score >= 58) return 'D';
  return 'F';
}

function getLapKey(lapLike) {
  if (!lapLike) return 'unknown';
  return lapLike.driverLabel ? `${lapLike.driverLabel}:${lapLike.lapNum}` : `${lapLike.lapNum}`;
}

function averageMetric(items, key) {
  return average(items.map((item) => item?.[key]).filter((value) => Number.isFinite(value)));
}

export function hasThrottleData(laps) {
  return (laps || []).some((lap) =>
    (lap?.data || []).some((sample) => {
      const value = Number(sample?.throttle);
      return Number.isFinite(value) && value > 1;
    })
  );
}

function hasGData(laps, field = 'longG') {
  return (laps || []).some((lap) =>
    (lap?.data || []).some((sample) => {
      const value = Number(sample?.[field]);
      return Number.isFinite(value) && Math.abs(value) > 0.01;
    })
  );
}

// Determine whether positive or negative G represents acceleration
function inferGAccelDirection(laps = [], gField = 'longG') {
  let positiveScore = 0;
  let negativeScore = 0;

  laps.forEach((lap) => {
    const samples = lap?.data || [];
    for (let i = 1; i < samples.length; i += 1) {
      const prev = samples[i - 1];
      const curr = samples[i];
      const prevSpeed = Number(prev?.speed);
      const currSpeed = Number(curr?.speed);
      const gValue = Number(curr?.[gField]);

      if (!Number.isFinite(prevSpeed) || !Number.isFinite(currSpeed) || !Number.isFinite(gValue)) continue;

      const speedGain = currSpeed - prevSpeed;
      if (speedGain <= 0.1) continue;

      if (gValue >= 0) {
        positiveScore += speedGain * Math.abs(gValue);
      } else {
        negativeScore += speedGain * Math.abs(gValue);
      }
    }
  });

  return positiveScore >= negativeScore ? 1 : -1;
}

function getThrottleValue(sample, sourceUsed, longGAccelDirection) {
  if (sourceUsed === 'throttle') {
    const value = Number(sample?.throttle);
    return Number.isFinite(value) ? value : null;
  }
  // G-force source: map acceleration G to a 0-100 pseudo-throttle scale
  const gValue = Number(sample?.longG);
  if (!Number.isFinite(gValue)) return null;
  const accelG = gValue * longGAccelDirection;
  return Math.max(0, accelG);
}

function chooseThrottleSource(laps) {
  if (hasThrottleData(laps)) return 'throttle';
  if (hasGData(laps, 'longG')) return 'longG';
  return null;
}

function getThrottleSourceMeta(sourceUsed) {
  if (sourceUsed === 'longG') {
    return { sourceLabel: 'Longitudinal G-Force', peakLabel: 'Peak Accel', peakUnit: 'G' };
  }
  return { sourceLabel: 'Throttle Position', peakLabel: 'Peak', peakUnit: '%' };
}

function getThrottleThresholds(sourceUsed) {
  return sourceUsed === 'longG' ? LONG_G_THROTTLE_THRESHOLDS : THROTTLE_THRESHOLDS;
}

function findWindowStartIndex(samples, startIdx, segmentStartDist) {
  let idx = startIdx;
  while (idx > 0) {
    const distFeet = toFeet(segmentStartDist - (Number(samples[idx - 1]?.dist) || 0));
    if (distFeet > LOOKBACK_FEET) break;
    idx -= 1;
  }
  return idx;
}

function findWindowEndIndex(samples, stopIdx, segmentStopDist) {
  let idx = stopIdx;
  while (idx < samples.length - 1) {
    const distFeet = toFeet((Number(samples[idx + 1]?.dist) || 0) - segmentStopDist);
    if (distFeet > LOOKAHEAD_FEET) break;
    idx += 1;
  }
  return idx;
}

function findMinSpeedIndex(samples, startIdx, endIdx) {
  let minIdx = startIdx;
  let minSpeed = Infinity;
  for (let i = startIdx; i <= endIdx; i += 1) {
    const speed = Number(samples[i]?.speed);
    if (Number.isFinite(speed) && speed < minSpeed) {
      minSpeed = speed;
      minIdx = i;
    }
  }
  return { minIdx, minSpeed };
}

function findThrottleOnsetIndex(samples, searchStart, endIdx, sourceUsed, longGAccelDirection) {
  const thresholds = getThrottleThresholds(sourceUsed);
  for (let i = searchStart; i <= endIdx; i += 1) {
    const throttle = getThrottleValue(samples[i], sourceUsed, longGAccelDirection);
    if (throttle == null) continue;
    if (throttle >= thresholds.onset) {
      // Confirm it stays above for a few samples
      let confirmed = 0;
      for (let j = i; j <= Math.min(i + 4, endIdx); j += 1) {
        const t = getThrottleValue(samples[j], sourceUsed, longGAccelDirection);
        if (t != null && t >= thresholds.onset) confirmed += 1;
      }
      if (confirmed >= 3) return i;
    }
  }
  return -1;
}

function findFullThrottleIndex(samples, onsetIdx, endIdx, sourceUsed, longGAccelDirection) {
  const thresholds = getThrottleThresholds(sourceUsed);
  for (let i = onsetIdx; i <= endIdx; i += 1) {
    const throttle = getThrottleValue(samples[i], sourceUsed, longGAccelDirection);
    if (throttle != null && throttle >= thresholds.full) {
      return i;
    }
  }
  return -1;
}

function detectHesitations(samples, onsetIdx, endIdx, sourceUsed, longGAccelDirection) {
  const thresholds = getThrottleThresholds(sourceUsed);
  const hesitations = [];
  let wasAboveOnset = false;
  let peakBeforeDrop = 0;
  // For longG source, lower the "meaningful peak" threshold
  const meaningfulPeak = sourceUsed === 'longG' ? thresholds.onset * 2 : 20;

  for (let i = onsetIdx; i <= endIdx; i += 1) {
    const throttle = getThrottleValue(samples[i], sourceUsed, longGAccelDirection);
    if (throttle == null) continue;

    if (throttle >= thresholds.onset) {
      if (!wasAboveOnset) wasAboveOnset = true;
      if (throttle > peakBeforeDrop) peakBeforeDrop = throttle;
    }

    if (wasAboveOnset && peakBeforeDrop > meaningfulPeak && throttle < peakBeforeDrop - thresholds.hesitationDrop) {
      hesitations.push({
        index: i,
        dropPct: sourceUsed === 'longG'
          ? ((peakBeforeDrop - throttle) / Math.max(peakBeforeDrop, 0.01)) * 100
          : peakBeforeDrop - throttle,
        dist: Number(samples[i]?.dist) || 0,
      });
      peakBeforeDrop = throttle;
    }
  }

  return hesitations;
}

function buildCornerNote(row) {
  if (row.hesitationCount >= 2) return 'Multiple throttle hesitations detected';
  if (row.hesitationCount === 1) return 'Throttle hesitation (lift-reapply) detected';
  if ((row.applicationSpreadFt ?? 0) >= 25) return 'Throttle pickup point varies lap to lap';
  if ((row.rampDistSpreadFt ?? 0) >= 30) return 'Time to full throttle is inconsistent';
  if ((row.score ?? 0) >= 88) return 'Clean, repeatable throttle application';
  return 'Mostly consistent with small variation';
}

function buildScore({ applicationSpreadFt, rampDistSpreadFt, fullThrottleSpreadFt, hesitationCount }) {
  const appPenalty = Math.min(1, (applicationSpreadFt || 0) / 35);
  const rampPenalty = Math.min(1, (rampDistSpreadFt || 0) / 40);
  const fullPenalty = Math.min(1, (fullThrottleSpreadFt || 0) / 50);
  const hesitationPenalty = Math.min(1, (hesitationCount || 0) / 3);

  const penalty = appPenalty * 0.35 + rampPenalty * 0.25 + fullPenalty * 0.15 + hesitationPenalty * 0.25;
  return Math.max(0, Math.min(100, Math.round(100 - penalty * 100)));
}

function summarizeCorner(segment, events) {
  const row = {
    cornerId: segment.label,
    label: segment.label,
    eventCount: events.length,
    applicationAvgFt: averageMetric(events, 'applicationFt'),
    applicationSpreadFt: spread(events.map((e) => e.applicationFt).filter(Number.isFinite)),
    rampDistAvgFt: averageMetric(events, 'rampDistFt'),
    rampDistSpreadFt: spread(events.map((e) => e.rampDistFt).filter(Number.isFinite)),
    fullThrottleAvgFt: averageMetric(events, 'fullThrottleFt'),
    fullThrottleSpreadFt: spread(events.map((e) => e.fullThrottleFt).filter(Number.isFinite)),
    peakThrottleAvg: averageMetric(events, 'peakThrottle'),
    hesitationCount: Math.round(average(events.map((e) => e.hesitationCount)) || 0),
    avgHesitationDropPct: averageMetric(events, 'avgHesitationDropPct'),
    minSpeedAvgMph: averageMetric(events, 'minSpeedMph'),
    exitSpeedAvgMph: averageMetric(events, 'exitSpeedMph'),
    throttleAtMinSpeedAvg: averageMetric(events, 'throttleAtMinSpeed'),
    fastestLapTimeSec: Math.min(...events.map((e) => e.lapTimeSec).filter(Number.isFinite)),
    reachesFullThrottle: events.some((e) => e.fullThrottleFt != null),
    laps: events,
  };

  row.score = buildScore(row);
  row.grade = gradeFromScore(row.score);
  row.note = buildCornerNote(row);
  return row;
}

export function detectThrottleZones({
  lap,
  segments,
  thresholdFeet = SEARCH_THRESHOLD_FEET,
  sourceUsed = 'throttle',
  longGAccelDirection = 1,
}) {
  const samples = lap?.fullData || lap?.data || [];
  if (!samples.length || !segments?.length) return [];

  return segments.map((segment) => {
    const startMatch = findClosestSampleIndex(samples, segment.startLat, segment.startLng, 0);
    if (startMatch.index < 0 || startMatch.distanceFeet > thresholdFeet) return null;

    const stopMatch = findClosestSampleIndex(samples, segment.stopLat, segment.stopLng, startMatch.index + 1);
    if (stopMatch.index < 0 || stopMatch.distanceFeet > thresholdFeet) return null;

    const segmentStartDist = Number(samples[startMatch.index]?.dist) || 0;
    const segmentStopDist = Number(samples[stopMatch.index]?.dist) || segmentStartDist;

    const windowStart = findWindowStartIndex(samples, startMatch.index, segmentStartDist);
    const windowEnd = findWindowEndIndex(samples, stopMatch.index, segmentStopDist);

    // Find minimum speed point in the segment — throttle application typically follows this
    const { minIdx, minSpeed } = findMinSpeedIndex(samples, startMatch.index, stopMatch.index);
    if (!Number.isFinite(minSpeed)) return null;

    // Search for throttle onset starting slightly before min speed point
    const searchStart = Math.max(windowStart, minIdx - 5);
    const onsetIdx = findThrottleOnsetIndex(samples, searchStart, windowEnd, sourceUsed, longGAccelDirection);
    if (onsetIdx < 0) return null;

    const fullIdx = findFullThrottleIndex(samples, onsetIdx, windowEnd, sourceUsed, longGAccelDirection);
    const hesitations = detectHesitations(samples, onsetIdx, windowEnd, sourceUsed, longGAccelDirection);

    const onsetSample = samples[onsetIdx];
    const minSpeedSample = samples[minIdx];
    const fullSample = fullIdx >= 0 ? samples[fullIdx] : null;
    const exitSample = samples[stopMatch.index];
    const onsetDist = Number(onsetSample?.dist) || 0;
    const minDist = Number(minSpeedSample?.dist) || 0;
    const fullDist = fullSample ? (Number(fullSample.dist) || 0) : null;

    // Peak throttle value in the window
    let peakThrottle = 0;
    for (let i = onsetIdx; i <= windowEnd; i += 1) {
      const t = getThrottleValue(samples[i], sourceUsed, longGAccelDirection);
      if (t != null && t > peakThrottle) peakThrottle = t;
    }

    // Throttle value at the minimum speed point
    const rawAtMin = getThrottleValue(minSpeedSample, sourceUsed, longGAccelDirection);

    return {
      cornerId: segment.label,
      label: segment.label,
      lapNum: lap?.lapNum ?? null,
      lapKey: getLapKey(lap),
      lapTimeSec: Number(lap?.lapTime),
      driverLabel: lap?.driverLabel || null,
      applicationFt: Math.max(0, toFeet(onsetDist - minDist)),
      rampDistFt: fullDist != null ? Math.max(0, toFeet(fullDist - onsetDist)) : null,
      fullThrottleFt: fullDist != null ? Math.max(0, toFeet(fullDist - segmentStartDist)) : null,
      peakThrottle,
      hesitationCount: hesitations.length,
      avgHesitationDropPct: hesitations.length
        ? average(hesitations.map((h) => h.dropPct))
        : null,
      minSpeedMph: minSpeed,
      exitSpeedMph: Number.isFinite(Number(exitSample?.speed)) ? Number(exitSample.speed) : null,
      throttleAtMinSpeed: rawAtMin != null ? rawAtMin : null,
      onsetIndex: onsetIdx,
      fullIndex: fullIdx,
    };
  }).filter(Boolean);
}

export function buildThrottleReferenceSet({ corners, mode = 'best-3' }) {
  const rows = Array.isArray(corners) ? corners : [];
  const maxEvents = mode === 'best-lap' ? 1 : 3;

  return rows.reduce((acc, corner) => {
    const ranked = (corner?.laps || [])
      .filter((e) => Number.isFinite(e?.lapTimeSec))
      .sort((a, b) => a.lapTimeSec - b.lapTimeSec);

    const selected = ranked.slice(0, maxEvents);
    if (!selected.length) {
      acc[corner.cornerId] = null;
      return acc;
    }

    acc[corner.cornerId] = {
      cornerId: corner.cornerId,
      label: corner.label,
      mode,
      selectedLapCount: selected.length,
      applicationAvgFt: averageMetric(selected, 'applicationFt'),
      rampDistAvgFt: averageMetric(selected, 'rampDistFt'),
      fullThrottleAvgFt: averageMetric(selected, 'fullThrottleFt'),
      peakThrottleAvg: averageMetric(selected, 'peakThrottle'),
      minSpeedAvgMph: averageMetric(selected, 'minSpeedMph'),
      exitSpeedAvgMph: averageMetric(selected, 'exitSpeedMph'),
      hesitationCount: Math.round(average(selected.map((e) => e.hesitationCount)) || 0),
      lapKeys: selected.map((e) => e.lapKey),
    };

    return acc;
  }, {});
}

export function compareCornerToReference({ corner, referenceCorner }) {
  if (!corner || !referenceCorner) return null;

  return {
    applicationDeltaFt: (corner.applicationAvgFt ?? 0) - (referenceCorner.applicationAvgFt ?? 0),
    rampDistDeltaFt: (corner.rampDistAvgFt ?? 0) - (referenceCorner.rampDistAvgFt ?? 0),
    fullThrottleDeltaFt: (corner.fullThrottleAvgFt ?? 0) - (referenceCorner.fullThrottleAvgFt ?? 0),
    exitSpeedDeltaMph: (corner.exitSpeedAvgMph ?? 0) - (referenceCorner.exitSpeedAvgMph ?? 0),
    minSpeedDeltaMph: (corner.minSpeedAvgMph ?? 0) - (referenceCorner.minSpeedAvgMph ?? 0),
  };
}

export function analyzeThrottle({
  laps,
  trackCoordinates,
  distanceUnit = 'ft',
}) {
  const safeLaps = Array.isArray(laps) ? laps.filter((lap) => lap?.data?.length) : [];
  const segments = extractTrackSegments(trackCoordinates);

  const sourceUsed = chooseThrottleSource(safeLaps);
  if (!safeLaps.length || !segments.length || !sourceUsed) {
    return {
      hasData: false,
      sourceUsed: null,
      sourceLabel: null,
      distanceUnit,
      referenceMode: 'best-3',
      referenceLabel: 'Best 3 laps',
      sessionSummary: null,
      referenceSets: { bestLap: {}, best3: {} },
      corners: [],
    };
  }

  const sourceMeta = getThrottleSourceMeta(sourceUsed);
  const longGAccelDirection = sourceUsed === 'longG' ? inferGAccelDirection(safeLaps, 'longG') : 1;
  const eventsByCorner = new Map();

  safeLaps.forEach((lap) => {
    detectThrottleZones({ lap, segments, sourceUsed, longGAccelDirection }).forEach((event) => {
      const key = event.cornerId;
      if (!eventsByCorner.has(key)) eventsByCorner.set(key, []);
      eventsByCorner.get(key).push(event);
    });
  });

  const rawCorners = segments
    .map((segment) => {
      const events = eventsByCorner.get(segment.label) || [];
      if (!events.length) return null;
      return summarizeCorner(segment, events);
    })
    .filter(Boolean);

  if (!rawCorners.length) {
    return {
      hasData: true,
      sourceUsed,
      sourceLabel: sourceMeta.sourceLabel,
      distanceUnit,
      referenceMode: 'best-3',
      referenceLabel: 'Best 3 laps',
      sessionSummary: null,
      referenceSets: { bestLap: {}, best3: {} },
      corners: [],
    };
  }

  const bestLapRefSet = buildThrottleReferenceSet({ corners: rawCorners, mode: 'best-lap' });
  const best3RefSet = buildThrottleReferenceSet({ corners: rawCorners, mode: 'best-3' });

  const corners = rawCorners.map((corner) => {
    const bestLapReference = bestLapRefSet[corner.cornerId] || null;
    const best3Reference = best3RefSet[corner.cornerId] || null;

    return {
      ...corner,
      bestLapReference,
      best3Reference,
      bestLapDelta: compareCornerToReference({ corner, referenceCorner: bestLapReference }),
      best3Delta: compareCornerToReference({ corner, referenceCorner: best3Reference }),
    };
  });

  const worstCorner = corners.reduce((worst, c) => (
    !worst || (c.applicationSpreadFt ?? 0) > (worst.applicationSpreadFt ?? 0) ? c : worst
  ), null);

  const bestCorner = corners.reduce((best, c) => (
    !best || (c.score ?? 0) > (best.score ?? 0) ? c : best
  ), null);

  return {
    hasData: true,
    sourceUsed,
    sourceLabel: sourceMeta.sourceLabel,
    distanceUnit,
    referenceMode: 'best-3',
    referenceLabel: 'Best 3 laps',
    referenceSets: {
      bestLap: bestLapRefSet,
      best3: best3RefSet,
    },
    sessionSummary: {
      worstCornerId: worstCorner?.cornerId ?? null,
      worstCornerLabel: worstCorner?.label ?? null,
      maxApplicationSpread: worstCorner?.applicationSpreadFt ?? null,
      bestCornerId: bestCorner?.cornerId ?? null,
      bestCornerLabel: bestCorner?.label ?? null,
      avgApplicationSpread: average(corners.map((c) => c.applicationSpreadFt).filter((v) => v != null)),
      avgHesitations: average(corners.map((c) => c.hesitationCount).filter((v) => v != null)),
    },
    corners,
  };
}
